import "./Approach.css";
import { useEffect, useRef } from "react";

import {
  FaSearch,
  FaClipboardList,
  FaPencilRuler,
  FaCode,
  FaRocket,
} from "react-icons/fa";

const steps = [
  {
    title: "Discover & Understand",
    icon: <FaSearch />,
    description:
      "We start by listening. Through collaborative discussions, we take time to understand your business, goals, challenges, and users before recommending a solution.",
    difference:
      "We don't believe in one-size-fits-all solutions. Every project starts with understanding your unique needs so we can build technology that truly fits your business.",
  },

  {
    title: "Plan & Strategize",
    icon: <FaClipboardList />,
    description:
      "We define the project scope, choose the right technologies and create a roadmap aligned with your business objectives.",
    difference:
      "Thoughtful planning reduces risks, improves efficiency and ensures every decision supports your long-term goals.",
  },

  {
    title: "Design with Purpose",
    icon: <FaPencilRuler />,
    description:
      "We create intuitive, engaging and user-centered designs that balance functionality with exceptional user experience.",
    difference:
      "Every interface is crafted to be accessible, easy to use and aligned with your brand.",
  },

  {
    title: "Build & Innovate",
    icon: <FaCode />,
    description:
      "We transform ideas into secure, scalable and high-performing digital solutions using modern technologies.",
    difference:
      "Our solutions are reliable today and flexible enough to grow with your business tomorrow.",
  },

  {
    title: "Test, Launch & Support",
    icon: <FaRocket />,
    description:
      "Every solution undergoes thorough testing before launch, followed by continuous support and improvements.",
    difference:
      "We see every project as the beginning of a long-term partnership.",
  },
];

export default function Approach() {
  const timelineRef = useRef(null);

  useEffect(() => {
    const items = timelineRef.current.querySelectorAll(".timeline-item");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      {
        threshold: 0.25,
      },
    );

    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const handleGetStarted = () => {
    window.location.href = "/quote";
  };

  return (
    <section className="approach">
      <div className="glow glow-one"></div>
      <div className="glow glow-two"></div>

      <div className="section-title">
        <span>OUR APPROACH</span>

        <h2>
          How We Turn Ideas Into
          <br />
          <strong>Impactful Digital Solutions</strong>
        </h2>

        <p>
          Every project begins with understanding your business and ends with a
          solution designed to grow alongside you.
        </p>
      </div>

      <div className="timeline" ref={timelineRef}>
        <svg className="timeline-line" viewBox="0 0 200 1200">
          <path
            d="
            M100 0
            C20 150 180 250 100 400
            C20 550 180 650 100 800
            C20 950 180 1050 100 1200
            "
          />
        </svg>

        {steps.map((step, index) => (
          <div
            key={index}
            className={`timeline-item ${index % 2 === 0 ? "left" : "right"}`}
          >
            <div className="step-number">
              <span>STEP</span>

              <h1>{String(index + 1).padStart(2, "0")}</h1>
            </div>

            <div className="card">
              <div className="icon">{step.icon}</div>

              <div>
                <h3>{step.title}</h3>

                <p>{step.description}</p>
              </div>
            </div>

            <div className="difference">
              <strong>What makes us different:</strong>

              <p>{step.difference}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="approach-cta">
        <p>Ready to bring your idea to life?</p>

        <button className="cta-button" onClick={handleGetStarted}>
          Get Started
        </button>
      </div>
    </section>
  );
}
